import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type { Url } from "next/dist/shared/lib/router/router";
import Link from "next/link";
import React from "react";
import { MdExpandLess, MdExpandMore } from "react-icons/md";

export function Dropdown(props: Headless.MenuProps & { className?: string }) {
  return <Headless.Menu as="div" {...props} />;
}

export function DropdownButton({
  as = "button",
  expandible = false,
  children,
  onClick,
  className,
  arrowClassName,
  ...props
}: {
  className?: string;
  expandible?: boolean;
  arrowClassName?: string;
} & Omit<Headless.MenuButtonProps<"button">, "className">) {
  return (
    <Headless.MenuButton
      as={as}
      {...props}
      className={clsx(className, "group")}
      onClick={(e) => {
        if (onClick) {
          onClick(e);
        }
      }}
    >
      {children as React.ReactNode}

      {expandible && (
        <div className="ml-1 text-brand-tertiary">
          <MdExpandLess size="1.5rem" className={clsx("hidden group-data-[open]:block", arrowClassName)} />

          <MdExpandMore size="1.5rem" className={clsx("group-data-[open]:hidden", arrowClassName)} />
        </div>
      )}
    </Headless.MenuButton>
  );
}

export function DropdownMenu({
  anchor = "bottom",
  className,
  noGap = false,
  ...props
}: { className?: string; noGap?: boolean } & Omit<Headless.MenuItemsProps, "as" | "className">) {
  return (
    <Headless.MenuItems
      {...props}
      transition
      anchor={anchor}
      className={clsx(
        // Anchor positioning
        `[--anchor-gap:theme(spacing.0)] [--anchor-padding:theme(spacing.1)] data-[anchor~=start]:[--anchor-offset:-6px] data-[anchor~=end]:[--anchor-offset:6px] sm:data-[anchor~=start]:[--anchor-offset:-4px] sm:data-[anchor~=end]:[--anchor-offset:4px]`,
        {
          "[--anchor-gap:theme(spacing.2)]": !noGap,
        },
        // Base styles
        "isolate flex w-max flex-col rounded-lg p-1",
        "outline outline-2 outline-base-input",
        // Handle scrolling when menu won't fit in viewport
        "overflow-y-auto",
        // Popover background
        "bg-base-white backdrop-blur-xl",
        // Transitions
        "transition data-[closed]:data-[leave]:opacity-0 data-[leave]:duration-100 data-[leave]:ease-in",
        className,
      )}
    />
  );
}

export function DropdownItem({
  className,
  href,
  ...props
}: Readonly<{
  className?: string;
  href?: Url;
  onClick?: () => void;
  children: React.ReactNode;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
}>) {
  const classes = clsx(
    className,
    // Base styles
    "cursor-pointer rounded-lg p-1 focus:outline-none",
    // Text styles
    "text-left",
    // Focus
    "data-[focus]:bg-base-input",
    // Disabled state
    "data-[disabled]:opacity-50",
  );

  return (
    <Headless.MenuItem>
      {href ? (
        <Link {...props} href={href} className={classes} />
      ) : (
        <button type="button" {...props} className={classes} />
      )}
    </Headless.MenuItem>
  );
}

export function DropdownHeader({ className, ...props }: React.ComponentPropsWithoutRef<"div"> & { className: string }) {
  return <div {...props} className={clsx(className, "px-3.5 pt-2.5 col-span-5 pb-1 sm:px-3")} />;
}

export function DropdownSection({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSectionProps, "as" | "className">) {
  return (
    <Headless.MenuSection
      {...props}
      className={clsx(
        className,
        // Define grid at the section level instead of the item level if subgrid is supported
        "col-span-full supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]",
      )}
    />
  );
}

export function DropdownHeading({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuHeadingProps, "as" | "className">) {
  return (
    <Headless.MenuHeading
      {...props}
      className={clsx(className, "px-3.5 col-span-full grid grid-cols-[1fr,auto] gap-x-12 px-3 pb-1 pt-2")}
    />
  );
}

export function DropdownDivider({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSeparatorProps, "as" | "className">) {
  return (
    <Headless.MenuSeparator
      {...props}
      className={clsx(className, "mx-3.5 h-px col-span-full my-1 border-0 bg-base-black")}
    />
  );
}

export function DropdownLabel({
  className,
  ...props
}: { className?: string } & Omit<Headless.LabelProps, "as" | "className">) {
  return (
    <Headless.Label {...props} data-slot="label" className={clsx(className, "col-start-2 row-start-1")} {...props} />
  );
}

export function DropdownDescription({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, "as" | "className">) {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(className, "col-span-2 col-start-2 row-start-2 group-data-[focus]:text-base-white")}
    />
  );
}

export function DropdownShortcut({
  keys,
  className,
  ...props
}: { keys: string | string[]; className?: string } & Omit<Headless.DescriptionProps<"kbd">, "as" | "className">) {
  return (
    <Headless.Description
      as="kbd"
      {...props}
      className={clsx(className, "col-start-5 row-start-1 flex justify-self-end")}
    >
      {(Array.isArray(keys) ? keys : keys.split("")).map((char, index) => (
        <kbd
          key={index}
          className={clsx([
            // Make sure key names that are longer than one character (like "Tab") have extra space
            index > 0 && char.length > 1 && "pl-1",
          ])}
        >
          {char}
        </kbd>
      ))}
    </Headless.Description>
  );
}
